import React, {forwardRef, FunctionComponent, ReactNode} from "react";
import {theme, styled, H3, Text, FlexColumn} from "@reside/ui";
import {css} from "@emotion/react";

/** Keeps Load More above fixed bottom chrome (minimap ~60px + 15px offset, Stonly launcher). */
export const COLUMN_BOTTOM_INSET_PX = 60;

type Props = Readonly<{
  footer?: ReactNode;
  testId?: string;
  children: ReactNode;
}>;

export type ColumnHeaderProps = Readonly<{
  className?: string;
  title: ReactNode;
  subtitle: ReactNode;
  /** When true, tile grid shows a bottom border (cards have scrolled under sticky header). */
  scrolledUnder?: boolean;
}>;

export const ColumnHeader = forwardRef<HTMLElement, ColumnHeaderProps>(
  ({title, subtitle, className, scrolledUnder}, ref) => (
    <header
      ref={ref}
      className={className}
      data-scrolled-under={scrolledUnder ? "true" : undefined}
    >
      <H3>{title}</H3>
      <Text color="gray100" fontSize={1}>
        {subtitle}
      </Text>
    </header>
  ),
);

ColumnHeader.displayName = "ColumnHeader";

export const Column: FunctionComponent<Props> = ({
  footer,
  children,
  testId = "",
}) => (
  <Wrapper data-test-id={testId}>
    <FlexColumnBody>
      {children}
      {footer && <footer>{footer}</footer>}
    </FlexColumnBody>
  </Wrapper>
);

const columnShellStyle = css`
  background-color: ${theme.color.lightBlue10};
  border-radius: 8px;
  position: relative;
  border: 1px solid ${theme.color.borderGray};
  border-top: none;

  header {
    padding: 8px 0 16px 16px;
    position: sticky;
    border-radius: 8px 8px 0 0;
    border-top: 1px solid ${theme.color.borderGray};
    top: 0px;
    background-color: ${theme.color.lightBlue10};
    z-index: 1;

    &[data-scrolled-under="true"] {
      border-bottom: 3px solid ${theme.color.gray10};
      border-radius: 0;
    }
  }
`;

const columnPaddedStyle = css`
  ${columnShellStyle}
  padding: 10px;

  footer {
    padding: 0 15px ${COLUMN_BOTTOM_INSET_PX}px;
  }
`;

/** Tile grid: full-bleed header; wrap cards with this for 10px inset. */
export const ColumnCardsPadding = styled.div`
  flex: 1;
  min-height: 0;
  padding: 10px 10px ${COLUMN_BOTTOM_INSET_PX}px;
`;

export const ColumnBody = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  ${columnShellStyle}
`;

export const FlexColumnBody = styled(FlexColumn)`
  height: 100%;
  ${columnPaddedStyle}
`;

const Wrapper = styled.div`
  padding: 7px;
  width: 25%;
`;
